import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen,
  faLocationDot,
  faMagnifyingGlass,
  faStore,
} from '@fortawesome/free-solid-svg-icons';
import Loader from '../Loader/Loader';
import './tiendasdestacadas.css';

const filtros = [
  { id: 'todas', label: 'Todas' },
  { id: 'destacadas', label: 'Destacadas' },
  { id: 'con-productos', label: 'Con productos' },
];

const TiendasDestacadas = ({ variant = 'listing' }) => {
  const isFeatured = variant === 'featured';
  const [tiendas, setTiendas] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const obtenerTiendas = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const response = await fetch('/api/tiendas', { cache: 'no-store' });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Error al obtener tiendas');
        }

        setTiendas(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    obtenerTiendas();
  }, []);

  const tiendasFiltradas = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    return tiendas.filter((tienda) => {
      const coincideTexto = !termino || [
        tienda.Nombre,
        tienda.Descripcion,
        tienda.Ciudad,
        tienda.Provincia,
      ].filter(Boolean).some((valor) => String(valor).toLowerCase().includes(termino));

      const coincideFiltro = filtro === 'todas'
        || (filtro === 'destacadas' && tienda.tiendadestacada)
        || (filtro === 'con-productos' && Number(tienda.productos || 0) > 0);

      return coincideTexto && coincideFiltro;
    });
  }, [busqueda, filtro, tiendas]);

  const tiendasVisibles = isFeatured ? tiendas.slice(0, 4) : tiendasFiltradas;

  const ubicacion = (tienda) => [tienda.Ciudad, tienda.Provincia, tienda.Pais]
    .filter(Boolean)
    .join(', ');

  return (
    <main className={`tiendas-page${isFeatured ? ' tiendas-page--featured' : ''}`}>
      <section className="tiendas-page-heading">
        <div>
          <span>{isFeatured ? 'Vendedores y marcas' : 'Descubrí nuevas marcas'}</span>
          <h1>{isFeatured ? 'Tiendas destacadas' : 'Explorá las tiendas'}</h1>
          <p>
            {isFeatured
              ? 'Conocé tiendas activas y sus productos más interesantes dentro de Cannubis Store.'
              : 'Encontrá tiendas activas, conocé sus productos y elegí dónde comprar dentro de Cannubis Store.'}
          </p>
        </div>
        {isFeatured && (
          <Link to="/tiendas" className="tiendas-page-link">
            Ver todas las tiendas
            <span aria-hidden="true">→</span>
          </Link>
        )}
      </section>

      {!isFeatured && (
        <section className="tiendas-toolbar">
          <div className="tiendas-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="search"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar una tienda, ciudad o provincia"
            />
          </div>

          <div className="tiendas-filter" aria-label="Filtrar tiendas">
            {filtros.map((item) => (
              <button
                key={item.id}
                type="button"
                className={filtro === item.id ? 'active' : ''}
                onClick={() => setFiltro(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <section className="tiendas-state">
          <Loader />
          <p>Cargando tiendas</p>
        </section>
      ) : errorMessage ? (
        <section className="tiendas-state">
          <h2>No pudimos cargar las tiendas</h2>
          <p>{errorMessage}</p>
        </section>
      ) : tiendasVisibles.length === 0 ? (
        <section className="tiendas-state">
          <FontAwesomeIcon icon={faBoxOpen} />
          <h2>No hay tiendas para esos filtros</h2>
          <p>Proba con otra busqueda o volve a ver todas las tiendas.</p>
        </section>
      ) : (
        <section className="tiendas-grid">
          {tiendasVisibles.map((tienda) => (
            <Link key={tienda.Idtienda} className="tienda-card" to={`/tienda/${tienda.Idtienda}`}>
              <div className="tienda-card-media">
                {tienda.Imagen ? (
                  <img src={tienda.Imagen} alt={tienda.Nombre} loading="lazy" />
                ) : (
                  <FontAwesomeIcon icon={faStore} />
                )}
                {tienda.tiendadestacada && <span>Destacada</span>}
              </div>

              <div className="tienda-card-copy">
                <h2>{tienda.Nombre}</h2>
                <p>{tienda.Descripcion || 'Tienda activa en Cannubis Store.'}</p>
                <div className="tienda-card-meta">
                  <span>
                    <FontAwesomeIcon icon={faLocationDot} />
                    {ubicacion(tienda) || 'Ubicacion no informada'}
                  </span>
                  <strong>{tienda.productos || 0} producto{Number(tienda.productos || 0) === 1 ? '' : 's'}</strong>
                </div>
              </div>
            </Link>
          ))}
        </section>
      )}
    </main>
  );
};

export default TiendasDestacadas;
